import { useCallback, useMemo, useRef, useState } from "react";

import Actionbar from "src/components/common/actionbar/Authactionbar";
import LoadingOverlay from "src/components/common/form/LoadingOverlay";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { useCompanyStore } from "src/store/useCompanyStore";
import useUiuxStore from "src/store/useUiuxStore";


import { useMouseContent } from "src/utils/mouse/mouseContent";

import { AgGridReact } from "ag-grid-react";
import { Content_KSK2110_R01 } from "./Content_KSK2110_R01";
import { DetailTable_KSK2110_R01 } from "./DetailTable_KSK2110_R01";
import { KSK2110_R01DetailDto, KSK2110_R01HeaderDto, KSK2110_R01InspectDto, KSK2110_R01LotDto } from "./Dto_KSK2110_R01";
import { Info_KSK2110_R01 } from "./Info_KSK2110_R01";
import { LotModal_KSK2110_R01 } from "./LotModal_KSK2110_R01";
import { LotTable_KSK2110_R01 } from "./LotTable_KSK2110_R01";
import { Modal_KSK2110_R01 } from "./Modal_KSK2110_R01";
import { useKSK2110_R01Store } from "./Store_KSK2110_R01";
import { WorkTimeModal_KSK2110_R01 } from "./WorkTimeModal_KSK2110_R01";
import { formatDateWithTime } from "src/utils/string/stringUtil";


export const Container_KSK2110_R01 = () => {

	const { 
		editKSK2110_R01,
		initData, syncInitialData,
		isDetailMaximized, setIsDetailMaximized,
	} = useKSK2110_R01Store();

	const { companyList } = useCompanyStore();
	const { moneyCdList, vatTypeList } = useCommonCodeStore();
	const { tabList, setMessage } = useUiuxStore();


	const RESIZER_HEIGHT = {
		code: 'KSK2110_R01',
		type: 'height',
		storageKey: 'KSK2110_R01-info-split-height',
		min: 5,
		max: 80,
		default: 60
	} as const;

	const RESIZER_LOT = {
		code: 'KSK2110_R01_lot',
		type: 'width',
		storageKey: 'KSK2110_R01-lot-split-width',
		min: 5,
		max: 80,
		default: 70
	} as const;

	const gridRef = useRef<AgGridReact<KSK2110_R01HeaderDto>>(null);
	const lotGridRef = useRef<AgGridReact<KSK2110_R01LotDto>>(null);
	const detailGridRef = useRef<AgGridReact<KSK2110_R01DetailDto>>(null);
	const inspectGridRef = useRef<AgGridReact<KSK2110_R01InspectDto>>(null);

	const containerRef = useRef<HTMLDivElement>(null);
	const infoAreaRef = useRef<HTMLDivElement>(null);
	const lotInfoRef = useRef<HTMLDivElement>(null);
	const bottomContainerRef = useRef<HTMLDivElement>(null);
	const isHeightResizing = useRef(false);
	const isLotResizing = useRef(false);

	const workStartEndHeight = '50';
	

	const {
		splitValue: heightSplitValue,
		handleMouseDown: handleHeightMouseDown,
	} = useMouseContent({
		resizer: RESIZER_HEIGHT,
		isResizing: isHeightResizing,
		containerRef,
		infoRef: infoAreaRef
	});

	const {
		splitValue: lotSplitValue,
		handleMouseDown: handleLotMouseDown,
	} = useMouseContent({
		resizer: RESIZER_LOT,
		isResizing: isLotResizing,
		containerRef,
		infoRef: lotInfoRef
	});
	

	const {
		handlePrintClick,
		handleExcelClick,
		// fetchData,
		fetchOne,
		defaultKSK2110_R01HeaderRow,
		defaultKSK2110_R01LotRow,
		defaultKSK2110_R01DetailRow,
		defaultKSK2110_R01InspectRow,
		initContainerData,
		handleDeleteClick,
		handleCreateClick,
		handleSaveClick,
		validateWorkStartEnd,
		handleWorkTimeClick,
		handleFetchBomProductList,
		getProductWorkFieldHandlers,
		handleFetchLotList,
		handleSearchLotList,
	} = Content_KSK2110_R01({
		gridRef,
		lotGridRef,
		detailGridRef,
		inspectGridRef,
		tabList, setMessage,
		containerRef,
		isResizing: isHeightResizing,
	});

	const isWorkStarted = useCallback(() => {
		return validateWorkStartEnd(editKSK2110_R01?.fromTime || undefined);
	}, [editKSK2110_R01?.fromTime]);

	const isWorkEnded = useCallback(() => {
		return validateWorkStartEnd(editKSK2110_R01?.toTime || undefined);
	}, [editKSK2110_R01?.toTime]);

	useMemo(() => { syncInitialData(); }, []);

	const headerDefaultRow = useMemo(() => defaultKSK2110_R01HeaderRow(), [defaultKSK2110_R01HeaderRow]);
	const lotDefaultRow = useMemo(() => defaultKSK2110_R01LotRow(), [defaultKSK2110_R01LotRow]);
	const detailDefaultRow = useMemo(() => defaultKSK2110_R01DetailRow(), [defaultKSK2110_R01DetailRow]);
	const inspectDefaultRow = useMemo(() => defaultKSK2110_R01InspectRow(), [defaultKSK2110_R01InspectRow]);

	return <div className={`content-container`}>
		<Actionbar
			onFetchClick={handleFetchLotList}
			onCreateClick={handleCreateClick}
			onDeleteClick={handleDeleteClick}
			onSaveClick={handleSaveClick}
			onPrintClick={handlePrintClick}
			onExcelClick={handleExcelClick}
			onInitClick={initContainerData}
		/>
		<WorkTimeModal_KSK2110_R01 handleSearchLotList={handleSearchLotList} handleWorkTimeClick={handleWorkTimeClick} />

		<LotModal_KSK2110_R01 handleSearchLotList={handleSearchLotList} />
		<Modal_KSK2110_R01 
			defaultKSK2110_R01HeaderRow={headerDefaultRow}
			inspectGridRef={inspectGridRef}
			defaultKSK2110_R01InspectRow={inspectDefaultRow}
			handleDeleteClick={handleDeleteClick}
		/>

		<div className="flex flex-1 p-[12px] gap-[4px] min-h-0 flex-col" ref={containerRef}>
			{!isDetailMaximized && (
					<>
					<div className="w-full max-w-full flex flex-1 gap-[4px]" style={{ height: `${heightSplitValue}%` }}>
						<div
							className="h-full max-h-full flex flex-wrap content-start pt-[12px] pl-[12px] pr-[12px] overflow-y-auto border border-gray-300"
							ref={lotInfoRef}
							style={{ width: `${lotSplitValue}%` }}
						>
							<Info_KSK2110_R01 editable={false}
								defaultKSK2110_R01HeaderRow={headerDefaultRow}
								handleWorkTimeClick={handleWorkTimeClick}
								handleFetchBomProductList={handleFetchBomProductList}
								handleFetchLotList={handleFetchLotList}
								getProductWorkFieldHandlers={getProductWorkFieldHandlers}
								validateWorkStartEnd={validateWorkStartEnd}
							/>
						</div>
						<div
							className="w-[6px] bg-gray-100 cursor-col-resize hover:bg-gray-300"
							onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => handleLotMouseDown('LR', e.nativeEvent)}
						/>
						<div className="h-full max-h-full flex flex-col min-h-0" style={{ width: `${100 - lotSplitValue}%` }}>
							<div className="w-full flex justify-start shrink-0 mb-[4px]" style={{ height: `${workStartEndHeight}px` }}>
								<button 
									className={`relative w-[205px] bg-blue-dark hover:bg-gray-400 text-white text-[18px] py-[1px] mr-[6px] rounded-[4px]`}
									onClick={() => handleWorkTimeClick(editKSK2110_R01?.productOrderNo || '', 'workStart')}
									>
									작업시작
									{!isWorkStarted() ? <br /> : ''}
									{!isWorkStarted() ? formatDateWithTime(editKSK2110_R01?.fromTime || undefined) : ''}
								</button>
								<button 
									className={`relative w-[205px] bg-blue-dark hover:bg-gray-400 text-white text-[18px] py-[1px] rounded-[4px]`}
									onClick={() => handleWorkTimeClick(editKSK2110_R01?.productOrderNo || '', 'workEnd')}
									>
									작업종료
									{!isWorkEnded() ? <br /> : ''}
									{!isWorkEnded() ? formatDateWithTime(editKSK2110_R01?.toTime || undefined) : ''}
								</button>
							</div>
							<div className="w-full flex-1 min-h-0">
								<LotTable_KSK2110_R01 lotGridRef={lotGridRef} defaultKSK2110_R01LotRow={lotDefaultRow} />
							</div>
						</div>
					</div>

					{/* 상하 리사이저 */}
					<div
						className="h-[6px] bg-gray-100 cursor-row-resize hover:bg-gray-300"
						onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => handleHeightMouseDown('UD', e.nativeEvent)}
					/>
				</>
			)}
			{/* 하단: DetailTable */}
			<div
				ref={infoAreaRef}
				style={{ height: isDetailMaximized ? '100%' : `${100 - heightSplitValue}%` }}
			>
				<div className="flex h-full gap-[4px]" ref={bottomContainerRef}>
					<DetailTable_KSK2110_R01
						gridRef={detailGridRef}
						defaultKSK2110_R01DetailRow={detailDefaultRow}
						isMaximized={isDetailMaximized}
						onToggleMaximize={() => setIsDetailMaximized(!isDetailMaximized)}
					/>
				</div>
			</div>

		</div>
	</div>
}